import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { isProduction } from '@backoffice-frontend/environment'
import { CheckmarkIcon, WarningIcon } from '@moia-dev/pace-icons'
import { Combobox, LoadingIndicator, Text } from '@moia-dev/pace-web'
import { useMemo, useState } from 'react'
import { vtrLocationMatchesCity } from '../utils'
import { buildMockVtrData, MOCK_VTR_KEY } from './mockVtrData'
import type { RouteBuilderVtrDataQuery } from './TripEditor.hook'
import {
  useRouteBuilderVtrDataLazyQuery,
  useRouteBuilderVtrTicketsQuery,
} from './TripEditor.hook'
import { VtrSelectionModal } from './VtrSelectionModal'

type VtrData = NonNullable<RouteBuilderVtrDataQuery['vtrData']>

type Ticket = {
  key: string
  status: string
  summary: string
  location: string
  missionType: string
}

type VtrInlineSelectorProps = {
  metropolisName: string | undefined
  onConfirm: (vtrData: VtrData, cbIndex: number, ticketKey: string) => void
}

export const VtrInlineSelector = ({
  metropolisName,
  onConfirm,
}: VtrInlineSelectorProps) => {
  const [query, setQuery] = useState('')
  const [pendingTicketKey, setPendingTicketKey] = useState<string | null>(null)
  const [mockVtrData, setMockVtrData] = useState<VtrData | null>(null)

  const { data } = useErrorHandlingQuery(useRouteBuilderVtrTicketsQuery, {
    variables: {},
  })

  const [fetchVtrData, { data: vtrDataResult, loading: vtrLoading }] =
    useRouteBuilderVtrDataLazyQuery()

  const mockTicket: Ticket = useMemo(
    () => ({
      key: MOCK_VTR_KEY,
      status: 'Open',
      summary: 'Mock — All ODD Element Types',
      location: metropolisName ?? 'Unknown',
      missionType: 'V&V',
    }),
    [metropolisName],
  )

  const realTickets: Ticket[] = useMemo(
    () =>
      (data?.vtrTickets ?? []).filter(t =>
        vtrLocationMatchesCity(t.location, metropolisName),
      ),
    [data?.vtrTickets, metropolisName],
  )

  // Gated on environment rather than on whether real tickets exist: PRD/PRD_US must
  // never show a ticket that doesn't talk to the backend (it could be mistaken for real
  // data), but DEV/INT need it available on demand to exercise CB selection and route
  // generation even after real VTR data shows up for a metropolis.
  const allTickets: Ticket[] = isProduction()
    ? realTickets
    : [...realTickets, mockTicket]

  const filteredTickets = query.trim()
    ? allTickets.filter(t => {
        const q = query.toLowerCase()
        return (
          t.key.toLowerCase().includes(q) ||
          t.summary.toLowerCase().includes(q) ||
          t.location.toLowerCase().includes(q)
        )
      })
    : allTickets

  const handleSelectTicket = (ticket: Ticket | null) => {
    if (!ticket) return
    setPendingTicketKey(ticket.key)
    setQuery('')

    if (ticket.key === MOCK_VTR_KEY) {
      // Use client-side mock data instead of fetching from server
      setMockVtrData(
        buildMockVtrData(metropolisName ?? 'Unknown') as unknown as VtrData,
      )
    } else {
      setMockVtrData(null)
      fetchVtrData({ variables: { jiraTicketNumber: ticket.key } })
    }
  }

  const isMockSelected = pendingTicketKey === MOCK_VTR_KEY
  const resolvedVtrData = isMockSelected ? mockVtrData : vtrDataResult?.vtrData
  const isLoading = vtrLoading && !isMockSelected

  const handleConfirmCb = (cbIndex: number) => {
    if (!pendingTicketKey || !resolvedVtrData) return
    onConfirm(resolvedVtrData, cbIndex, pendingTicketKey)
    setPendingTicketKey(null)
    setMockVtrData(null)
  }

  return (
    <div
      css={{
        position: 'relative',
        // Suppress the blue focus ring that pace-web adds via hasFocus class
        '& [class*="hasFocus"]': {
          outline: 'none !important',
        },
        // Animate the chevron rotation (transition on the always-present arrow class,
        // so it plays in both directions — not just when flipIcon is added)
        '& [class*="arrow"]': { transition: 'transform 200ms ease' },
        // Hide the chevron while loading
        ...(isLoading && pendingTicketKey
          ? { '& [class*="toggle"]': { opacity: 0 } }
          : {}),
      }}
    >
      {isLoading && pendingTicketKey && (
        <div
          css={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <LoadingIndicator size={16} />
        </div>
      )}
      <Combobox
        id="vtr-ticket-selector"
        value={null}
        options={filteredTickets}
        optionToId={t => t?.key ?? ''}
        optionToQuery={t =>
          `${t?.key ?? ''} ${t?.summary ?? ''} ${t?.location ?? ''}`
        }
        placeholder="Select VTR Ticket to auto-generate route"
        query={query}
        onQueryChange={setQuery}
        onChange={handleSelectTicket}
        emptyState="No tickets found"
        formatOption={t => t?.key ?? ''}
        disabled={isLoading && !!pendingTicketKey}
        renderOption={t => {
          if (!t) return null
          const matched = vtrLocationMatchesCity(t.location, metropolisName)
          const description = [t.summary, t.location]
            .filter(Boolean)
            .join(' @ ')
          return (
            <div>
              <div css={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {matched ? (
                  <CheckmarkIcon
                    css={theme => ({
                      width: 14,
                      height: 14,
                      color: theme.semantic.ColorSuccessDefault,
                      flexShrink: 0,
                    })}
                  />
                ) : (
                  <WarningIcon
                    css={theme => ({
                      width: 14,
                      height: 14,
                      color: theme.semantic.ColorWarningDefault,
                      flexShrink: 0,
                    })}
                  />
                )}
                <Text size="sm" weight="bold">
                  {t.key}
                </Text>
                <Text
                  size="sm"
                  css={theme => ({ color: theme.semantic.ColorContentReduced })}
                >
                  ({t.status})
                </Text>
              </div>
              {description && (
                <Text
                  size="sm"
                  css={theme => ({
                    color: theme.semantic.ColorContentReduced,
                    paddingLeft: 20,
                    display: 'block',
                  })}
                >
                  {description}
                </Text>
              )}
            </div>
          )
        }}
      />

      {/* CB selection modal — shown after ticket selected and data loaded */}
      {pendingTicketKey && !isLoading && resolvedVtrData && (
        <VtrSelectionModal
          vtrData={resolvedVtrData}
          onConfirm={handleConfirmCb}
          onCancel={() => {
            setPendingTicketKey(null)
            setMockVtrData(null)
          }}
        />
      )}
    </div>
  )
}
